import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, MessageCircle, ArrowRight } from "lucide-react";
import { webApi, errText } from "@/lib/webApi";

export function usePublicLinks() {
  const [links, setLinks] = useState({});
  useEffect(() => {
    webApi.get("/public").then((r) => setLinks(r.data)).catch(() => {});
  }, []);
  return links;
}

export function StartOnWhatsApp({ href }) {
  return (
    <p className="mt-8 text-sm text-[--w-text-2]" data-testid="start-on-whatsapp">
      New to Dicta?{" "}
      <a href={href} target="_blank" rel="noreferrer" data-testid="start-on-whatsapp-link" className="text-[--w-accent] underline-offset-4 hover:underline">
        Start on WhatsApp
      </a>
    </p>
  );
}

export function Shell({ children }) {
  return (
    <div className="theme-warm min-h-screen bg-[--w-bg] text-[--w-text]">
      <div className="mx-auto flex min-h-screen max-w-md flex-col justify-center px-6 py-12">
        <Link to="/welcome" className="mb-8 font-display text-2xl font-bold tracking-tight text-[--w-text]" data-testid="web-brand">Dicta</Link>
        <div className="rounded-[--w-radius] border border-[--w-border] bg-[--w-card] p-8 shadow-sm">
          {children}
        </div>
      </div>
    </div>
  );
}

export default function WebLogin() {
  const links = usePublicLinks();
  const nav = useNavigate();
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    webApi.get("/me").then(() => nav("/app", { replace: true })).catch(() => {});
  }, [nav]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await webApi.post("/auth/request", { phone });
      setDone(data);
    } catch (err) {
      setError(errText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Shell>
      <h1 className="text-3xl font-semibold tracking-tight text-[--w-text]">Sign in</h1>
      <p className="mt-3 text-sm text-[--w-text-2]">We'll send a one-time link to your WhatsApp. No password needed.</p>
      {!done && (
        <form onSubmit={submit} className="mt-8 space-y-3" data-testid="web-login-form">
          <label htmlFor="wa-phone" className="text-xs uppercase tracking-wide text-[--w-text-3]">WhatsApp number</label>
          <input id="wa-phone" data-testid="web-login-phone-input" required inputMode="tel" placeholder="+91 98765 43210" value={phone}
            onChange={(e) => setPhone(e.target.value)}
            className="w-full rounded-md border border-[--w-border] bg-[--w-card] px-4 py-3 text-base text-[--w-text] outline-none transition-colors duration-200 focus:border-[--w-accent]" />
          {error && <p className="text-sm text-red-700" data-testid="web-login-error">{error}</p>}
          <button type="submit" disabled={busy} data-testid="web-login-submit-button"
            className="flex w-full items-center justify-center gap-2 rounded-md bg-[--w-brand] text-white hover:bg-stone-800 px-4 py-3 font-semibold transition-colors duration-150 disabled:opacity-60">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />} Send link
          </button>
        </form>
      )}
      {done && (
        <div className="mt-8 rounded-[--w-radius] border border-[--w-border] bg-[--w-card-2] p-5" data-testid="web-login-sent">
          {!done.fallback_only && <p className="text-[--w-text]">{done.message}</p>}
          <p className="mt-3 text-sm text-[--w-text-2]">
            {done.fallback_only ? "Get your link straight from WhatsApp:" : "No message after a minute? WhatsApp only lets us message you first within 24 hours of your last message. Get the link straight from the chat:"}
          </p>
          <a href={links.wa_login} target="_blank" rel="noreferrer" data-testid="send-login-on-whatsapp-button"
            className="mt-4 inline-flex items-center gap-2 rounded-md border border-[--w-accent] px-4 py-2 text-sm text-[--w-accent] transition-colors duration-200 hover:bg-blue-50">
            <MessageCircle className="h-4 w-4" /> Send LOGIN on WhatsApp
          </a>
          <button onClick={() => setDone(null)} data-testid="web-login-try-again" className="ml-3 text-sm text-[--w-text-3] hover:text-[--w-text-2]">Use another number</button>
        </div>
      )}
      <StartOnWhatsApp href={links.wa_start} />
    </Shell>
  );
}
